import { useState, ReactElement } from 'react';
import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import { type FormatDateOptions, FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { Modal } from '@alltrails/modal';
import classNames from 'classnames';
import { StaticImageData } from 'next/image';
import { useLanguageCode } from '@alltrails/language/hooks/useLanguageCode';
import AppIcon from '@alltrails/denali/components/AppIcon';
import type { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import styles from './styles/styles.module.scss';

export type ArticleCard = {
  cardType: 'article';
  logoSrc: StaticImageData;
  company: string;
  title: string;
  date: string;
  href: string;
};

export type OldPressReleaseCard = {
  cardType: 'press-release';
  title: ReactElement;
  date: string;
  modalContent: ReactElement;
  languages?: LanguageRegionCode[];
};

type PressCardProps = ArticleCard | OldPressReleaseCard;

const ICON_SIZE = 32;

const OldPressCard = (props: PressCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { cardType, title, date } = props;
  const subheader = cardType === 'article' ? props.company : <FormattedMessage defaultMessage="Press release" />;

  const intl = useIntl();
  const formatOptions: FormatDateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const languageCode = useLanguageCode();

  return (
    <div className={styles.container}>
      <div
        className={classNames(styles.contentContainer, {
          [styles.pressRelease]: cardType === 'press-release',
          [styles.withHyphens]: languageCode === 'de' // hyphenate German titles due to long words
        })}
        lang={languageCode}
      >
        <div className={styles.logoContainer}>
          {cardType === 'article' ? (
            <Image alt="" src={props.logoSrc} loading="lazy" height={ICON_SIZE} width={ICON_SIZE} />
          ) : (
            <AppIcon size={ICON_SIZE} shape="circle" />
          )}
          <Typography variant="heading200">{subheader}</Typography>
        </div>
        <Typography variant="heading400" className={styles.heading}>
          {title}
        </Typography>
      </div>
      <div className={styles.contentContainer}>
        <Typography variant="paragraph200">{intl.formatDate(date, formatOptions)}</Typography>
        {cardType === 'article' ? (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            testId="press-card-link"
            title="Press card link"
            size="sm"
            className={styles.iconButton}
            linkInfo={{ href: props.href, target: '_blank' }}
          />
        ) : (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            testId="press-card-button"
            title="Press card button"
            size="sm"
            className={styles.iconButton}
            onClick={() => setIsOpen(true)}
          />
        )}
      </div>
      {cardType === 'press-release' && (
        <Modal size="lg" isOpen={isOpen} onCloseRequest={() => setIsOpen(false)}>
          <div className={styles.modalContainer}>
            <AppIcon className={styles.modalLogo} size={96} />
            {props.modalContent}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OldPressCard;
